import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";

import bowler_hand from "../../images/dashboard/Bowler_Hand_Icon.svg";
import ball_icon from "../../images/dashboard/Ball_Icon.svg";
import bowler_avatar from "../../images/dashboard/Bowler_Avatar_Empty.svg";
import theme from "../../theme/feedback";

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.lg};
  margin: ${theme.spacing.sm} 0 ${theme.spacing.md} 0;

  @media only screen and (max-width: 991px) and (orientation: portrait) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${theme.color.surface};
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.lg};
  box-shadow: 0px 2px 8px rgba(21, 34, 50, 0.06);
  padding: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0px 6px 18px rgba(21, 34, 50, 0.12);
    transform: translateY(-2px);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const CardTitle = styled.span`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeMd};
  font-weight: ${theme.font.weightMedium};
  color: ${theme.color.textMuted};
`;

const IconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${theme.radius.md};
  background: ${theme.color.surfaceAlt};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 22px;
    height: 22px;
  }
`;

const Value = styled.span`
  font-family: ${theme.font.family};
  font-size: 32px;
  font-weight: ${theme.font.weightBold};
  line-height: 1;
  color: ${theme.color.text};
`;

const TrendRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const TrendPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeSm};
  font-weight: ${theme.font.weightSemibold};
  padding: 2px 8px;
  border-radius: 999px;
  color: ${(props) => (props.up ? theme.color.success : theme.color.error)};
  background: ${(props) =>
    props.up ? theme.color.successBg : theme.color.errorBg};
`;

const TrendCaption = styled.span`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeSm};
  color: ${theme.color.textMuted};
`;

const StatCard = ({ title, value, trend, caption, icon }) => {
  const up = trend >= 0;
  return (
    <Card>
      <CardTop>
        <CardTitle>{title}</CardTitle>
        <IconBadge>{icon}</IconBadge>
      </CardTop>
      <Value>{value}</Value>
      <TrendRow>
        <TrendPill up={up}>
          {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          {Math.abs(trend)}%
        </TrendPill>
        <TrendCaption>{caption}</TrendCaption>
      </TrendRow>
    </Card>
  );
};

export const Filters = ({
  filtered_type = "day",
  dashboard_data,
}) => {
  const { t } = useTranslation();
  let descriptionText = t("message.info.dashboard_week");

  if (filtered_type === "day") {
    descriptionText = t("message.info.dashboard_day");
  } else if (filtered_type === "month") {
    descriptionText = t("message.info.dashboard_month");
  }

  // Standard period-over-period change: (current - previous) / previous.
  // The previous version divided by the current value, producing nonsense
  // figures like -200%. Guard divide-by-zero and treat "from nothing" as +100%.
  const get_average = (currentPeriod, lastPeriod) => {
    if (lastPeriod === 0) {
      return currentPeriod === 0 ? 0 : 100;
    }
    return Number(
      (((currentPeriod - lastPeriod) / lastPeriod) * 100).toFixed(0)
    );
  };

  const current = dashboard_data.current_totals;
  const previous = dashboard_data.previous_totals;

  return (
    <Row>
      <StatCard
        title={t("general.total_bowlers")}
        value={current.customers}
        trend={get_average(current.customers, previous.customers)}
        caption={descriptionText}
        icon={<img src={bowler_avatar} alt="" />}
      />
      <StatCard
        title={t("general.total_spec_sheets")}
        value={current.specsheets}
        trend={get_average(current.specsheets, previous.specsheets)}
        caption={descriptionText}
        icon={<img src={bowler_hand} alt="" />}
      />
      <StatCard
        title={t("general.total_balls")}
        value={current.ballsDrilled}
        trend={get_average(current.ballsDrilled, previous.ballsDrilled)}
        caption={descriptionText}
        icon={<img src={ball_icon} alt="" />}
      />
    </Row>
  );
};
